import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { tap, shareReplay, take, switchMap } from 'rxjs/operators';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

import { UserService } from 'src/app/services/shared/user.service';
import { ResourceService } from 'src/app/services/resource.service';
import { TerraUtils } from 'src/app/shared/TerraUtils';
import { OnboardingLpRequest } from './onboardingLpRequest.model';
import { PersonalDetailsReqRes } from 'src/app/account/models/personalDetailsReqRes.model';
import { CompanyProfileLpRequest } from '../lp-profile/company-profile-lp/companyProfileLpRequest.model';

export enum LpOnboardingStep {
  BasicDetails = 1,
  OrganizationDetails = 2
}

@Injectable()
export class OnboardingLpService extends OnDestroyMixin {
  private onboardingForm: UntypedFormGroup;

  allStates = this.resourceService.allStates;
  countryCodes = TerraUtils.consts.countryCode;
  USCountryId = this.resourceService.getCountryByCode(this.countryCodes.US).id;

  get personalDetailsForm() {
    if (!this.onboardingForm) {
      return null;
    }
    return this.onboardingForm.get('personal') as UntypedFormGroup;
  }
  get organizationDetailsForm() {
    if (!this.onboardingForm) {
      return null;
    }
    return this.onboardingForm.get('organization') as UntypedFormGroup;
  }

  accountDetails$ = this.userService.accountDetails$.pipe(
    tap(accountDetails => {
      if (!accountDetails.isRepresentingOrganization) {
        this.onboardingForm.get('personal.countryId').setValidators(Validators.required);
      }
    }),
    shareReplay());

  constructor(
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private resourceService: ResourceService) {
      super();
      this.generateForm();
  }

  generateForm() {

    const reqMin2Max30Validators = [Validators.required, Validators.maxLength(30), Validators.minLength(2)];

    this.onboardingForm = this.fb.group({
      personal: this.fb.group({
        firstName: [null, reqMin2Max30Validators],
        lastName: [null, reqMin2Max30Validators],
        phone1: [null, Validators.required],
        countryId: null,
        stateName: null
      }),
      organization: this.fb.group({
        name: [null, Validators.required],
        doingBusinessAs: [],
        registrationNumber: [null, Validators.required],
        roleInOrganization: [null, Validators.required],
        industry: [null, Validators.required],
        countryId: [null, Validators.required],
        stateName: [null]
      })
    });

    this.onboardingForm.get('personal.countryId').valueChanges.pipe(untilComponentDestroyed(this)).subscribe(countryId => {
      this.onboardingForm.get('personal.stateName').setValidators(countryId === this.USCountryId ? Validators.required : []);
      this.onboardingForm.get('personal.stateName').updateValueAndValidity();
    });

    this.onboardingForm.get('organization.countryId').valueChanges.pipe(untilComponentDestroyed(this)).subscribe(countryId => {
      this.onboardingForm.get('organization.stateName').setValidators(countryId === this.USCountryId ? Validators.required : []);
      this.onboardingForm.get('organization.stateName').updateValueAndValidity();
    });
  }


  generateSubmitModel(isOrganization: boolean): OnboardingLpRequest {

    const model = new OnboardingLpRequest();
    model.personalDetails = new PersonalDetailsReqRes();

    const personalValues = this.personalDetailsForm.value;

    model.personalDetails.firstName = personalValues.firstName;
    model.personalDetails.lastName = personalValues.lastName;
    model.personalDetails.phone1 = personalValues.phone1;
    if (!isOrganization) {
      model.personalDetails.countryId = personalValues.countryId;
      if (personalValues.countryId === this.USCountryId) {
        model.personalDetails.stateName = personalValues.stateName;
      }
    }

    if (isOrganization) {
      const organizationValues = this.organizationDetailsForm.value;
      model.organizationDetails = new CompanyProfileLpRequest();

      model.organizationDetails.name = organizationValues.name;
      model.organizationDetails.doingBusinessAs = organizationValues.doingBusinessAs;
      model.organizationDetails.registrationNumber = organizationValues.registrationNumber;
      model.organizationDetails.roleInOrganization = organizationValues.roleInOrganization;
      model.organizationDetails.industry = organizationValues.industry;
      model.organizationDetails.countryId = organizationValues.countryId;

      if (organizationValues.countryId === this.USCountryId) {
        model.organizationDetails.stateName = organizationValues.stateName;
      }
    }

    return model;
  }

  submitForm() {
    return this.userService.accountDetails$.pipe(take(1),
      switchMap(accountDetails => {
        const submitModel = this.generateSubmitModel(accountDetails.isRepresentingOrganization);
        return this.userService.onboardingLp(submitModel);
      })
    );
  }
}
